/* ===== Members Page ===== */

import { useEffect, useState } from 'react'
import type { Invitation, Membership, RoleName } from '@zetyar/domain'
import { ROLES, ROLE_LABELS } from '@zetyar/domain'
import { Check, Copy, Edit, Mail, Plus, UserPlus, Users, X } from 'lucide-react'
import { useApp } from '@/presentation/providers/useApp'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Badge } from '@/presentation/components/ui/Badge'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Modal } from '@/presentation/components/ui/Modal'
import { createWebTenantContext } from '@/infrastructure/repositories/announcements'
import { webMemberRepository } from '@/infrastructure/repositories/members'
import { webInvitationRepository } from '@/infrastructure/repositories/invitations'
import { useReauthGuard } from '@/presentation/hooks/useReauthGuard'

const STATUS_LABEL: Record<string, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  suspended: 'Suspendido',
}

const ROLE_OPTIONS: { value: RoleName; label: string }[] = Object.values(ROLES).map((value) => ({
  value,
  label: ROLE_LABELS[value],
}))

const ROLE_LABEL = ROLE_LABELS

const POSITION_PRESETS = [
  { value: 'none', label: 'Sin etiqueta de cargo' },
  { value: 'Presidente', label: 'Presidente' },
  { value: 'Vicepresidente', label: 'Vicepresidente' },
  { value: 'Tesorero', label: 'Tesorero' },
  { value: 'Secretario', label: 'Secretario' },
  { value: 'Director', label: 'Director' },
  { value: 'Capitán de Ruta', label: 'Capitán de Ruta' },
  { value: 'Miembro', label: 'Miembro' },
  { value: 'custom', label: 'Otro (especificar)' },
] as const

export function MembersPage() {
  const { currentMembership, currentOrganization, profile, user, permissions } = useApp()
  const [members, setMembers] = useState<Membership[]>([])
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<RoleName>(ROLES.MEMBER)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)

  const [showEditModal, setShowEditModal] = useState(false)
  const [editingMember, setEditingMember] = useState<Membership | null>(null)
  const [editDisplayName, setEditDisplayName] = useState('')
  const [editRoles, setEditRoles] = useState<RoleName[]>([])
  const [editStatus, setEditStatus] = useState<'active' | 'inactive' | 'suspended'>('active')
  const [editPositionCategory, setEditPositionCategory] = useState<string>('none')
  const [editCustomPosition, setEditCustomPosition] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)

  const isAdmin = profile?.isSuperAdmin || permissions.includes('members.manage')
  const { guard, reauthModal } = useReauthGuard()

  const tenantContext = () => {
    if (!currentOrganization || !profile || !user) return null
    return createWebTenantContext({
      authUid: user.uid,
      personId: profile.id,
      organizationId: currentOrganization.id,
      membershipId: currentMembership?.id,
    })
  }

  const loadAll = async () => {
    const context = tenantContext()
    if (!context) {
      setMembers([])
      setInvitations([])
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      const [memberResult, invitationResult] = await Promise.all([
        webMemberRepository.list(context, { limit: 100 }),
        webInvitationRepository.listPending(context, { limit: 50 }),
      ])
      setMembers(memberResult)
      setInvitations(invitationResult)
    } catch (error) {
      console.error('Error al cargar miembros e invitaciones:', error)
      setMembers([])
      setInvitations([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    let isMounted = true

    const run = async () => {
      if (!isMounted) return
      await loadAll()
    }

    void run()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMembership, currentOrganization, profile, user])

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    const context = tenantContext()
    if (!context) return

    try {
      await webInvitationRepository.create(context, {
        email: inviteEmail,
        role: inviteRole,
      })
      await loadAll()
    } catch (error) {
      console.error('Error al crear la invitacion:', error)
    }

    setShowInviteModal(false)
    setInviteEmail('')
    setInviteRole(ROLES.MEMBER)
  }

  const handleRevoke = async (invitationId: string) => {
    const context = tenantContext()
    if (!context) return

    try {
      await webInvitationRepository.revoke(context, invitationId)
      await loadAll()
    } catch (error) {
      console.error('Error al revocar la invitacion:', error)
    }
  }

  const openEditModal = (member: Membership) => {
    setEditingMember(member)
    setEditDisplayName(member.displayName ?? '')
    setEditRoles(member.roles)
    setEditStatus(member.status)

    if (member.position) {
      const isPreset = POSITION_PRESETS.some(
        (p) => p.value !== 'custom' && p.value !== 'none' && p.value.toLowerCase() === member.position?.toLowerCase(),
      )
      if (isPreset) {
        const matched = POSITION_PRESETS.find(
          (p) => p.value.toLowerCase() === member.position?.toLowerCase(),
        )
        setEditPositionCategory(matched?.value || member.position)
        setEditCustomPosition('')
      } else {
        setEditPositionCategory('custom')
        setEditCustomPosition(member.position)
      }
    } else {
      setEditPositionCategory('none')
      setEditCustomPosition('')
    }

    setShowEditModal(true)
  }

  const toggleEditRole = (role: RoleName) => {
    setEditRoles((current) =>
      current.includes(role) ? current.filter((r) => r !== role) : [...current, role],
    )
  }

  const handleEditSave = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingMember || editRoles.length === 0) return
    guard(() => performEditSave())
  }

  const performEditSave = async () => {
    if (!editingMember) return
    const context = tenantContext()
    if (!context) return

    try {
      setIsUpdating(true)

      const resolvedPosition =
        editPositionCategory === 'custom'
          ? editCustomPosition.trim() || null
          : editPositionCategory === 'none'
            ? null
            : editPositionCategory

      // Update details (display name, position, plus keep the denormalized primary role in sync)
      if (webMemberRepository.updateDetails) {
        await webMemberRepository.updateDetails(context, editingMember.id, {
          displayName: editDisplayName,
          role: editRoles[0],
          // '' clears the stored position server-side.
          position: resolvedPosition ?? '',
        })
      }

      // Diff and apply role changes individually
      const rolesToAdd = editRoles.filter((role) => !editingMember.roles.includes(role))
      const rolesToRemove = editingMember.roles.filter((role) => !editRoles.includes(role))

      if (webMemberRepository.assignRole) {
        for (const role of rolesToAdd) {
          await webMemberRepository.assignRole(context, editingMember.id, role)
        }
      }

      if (webMemberRepository.revokeRole) {
        for (const role of rolesToRemove) {
          const assignment = editingMember.roleAssignments?.find((item) => item.role === role)
          if (assignment) {
            await webMemberRepository.revokeRole(context, editingMember.id, assignment.id, role)
          }
        }
      }

      // Update status if it changed
      if (editStatus !== editingMember.status && webMemberRepository.updateStatus) {
        await webMemberRepository.updateStatus(context, editingMember.id, editStatus)
      }

      await loadAll()
      setShowEditModal(false)
      setEditingMember(null)
    } catch (error) {
      console.error('Error al editar miembro:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const copyInviteLink = (token: string) => {
    const link = `${window.location.origin}${window.location.pathname}#/accept-invite?token=${token}`
    void navigator.clipboard.writeText(link).then(() => {
      setCopiedToken(token)
      setTimeout(() => setCopiedToken((current) => (current === token ? null : current)), 2000)
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Miembros</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Personas activas en {currentOrganization?.name}
          </p>
        </div>
        {isAdmin && (
          <Button
            variant="primary"
            leftIcon={<UserPlus className="w-4 h-4" />}
            onClick={() => setShowInviteModal(true)}
          >
            Invitar miembro
          </Button>
        )}
      </div>

      {invitations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-600" /> Invitaciones pendientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {invitations.map((invitation) => (
                <div key={invitation.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                      {invitation.email}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      {ROLE_LABEL[invitation.role] ?? invitation.role} • Expira{' '}
                      {new Date(invitation.expiresAt).toLocaleDateString('es-CL')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={
                        copiedToken === invitation.token ? (
                          <Check className="w-3.5 h-3.5" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )
                      }
                      onClick={() => copyInviteLink(invitation.token)}
                    >
                      {copiedToken === invitation.token ? 'Copiado' : 'Copiar enlace'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<X className="w-3.5 h-3.5 text-danger-600" />}
                      onClick={() => void handleRevoke(invitation.id)}
                    >
                      Revocar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-5 h-5 text-primary-600" /> Directorio
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 animate-pulse">
              Cargando miembros desde Supabase...
            </p>
          ) : members.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              No hay miembros cargados en {currentOrganization?.name}.
            </p>
          ) : (
            <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {members.map((member) => (
                <div key={member.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0 bg-primary-50 dark:bg-primary-950/50 text-primary-600 dark:text-primary-200 font-semibold text-sm">
                      {(member.displayName ?? '?').charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                          {member.displayName}
                        </p>
                        {member.position && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60">
                            {member.position}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                        {member.institutionalEmail}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex flex-wrap gap-1 justify-end">
                      {member.roles.length === 0 ? (
                        <Badge variant="neutral">Sin rol</Badge>
                      ) : (
                        member.roles.map((role) => (
                          <Badge key={role} variant="neutral">
                            {ROLE_LABEL[role] ?? role}
                          </Badge>
                        ))
                      )}
                    </div>
                    {member.institutionalEmail && (
                      <a
                        href={`mailto:${member.institutionalEmail}`}
                        className="text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400"
                        aria-label="Enviar correo"
                      >
                        <Mail className="w-4 h-4" />
                      </a>
                    )}
                    <Badge variant={member.status === 'active' ? 'primary' : 'neutral'}>
                      {STATUS_LABEL[member.status] ?? member.status}
                    </Badge>
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditModal(member)}
                        className="h-8 w-8 p-0 flex items-center justify-center"
                        aria-label="Editar miembro"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Modal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} title="Invitar miembro">
        <form onSubmit={handleInvite} className="space-y-4">
          <Input
            label="Correo electronico"
            type="email"
            placeholder="correo@ejemplo.com"
            required
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Rol</label>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as RoleName)}
              className="w-full h-10 px-3 rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Se generara un enlace de invitacion para copiar y enviar manualmente. El envio
            automatico por correo aun no esta disponible.
          </p>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800">
            <Button variant="outline" type="button" onClick={() => setShowInviteModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" leftIcon={<Plus className="w-4 h-4" />}>
              Crear invitacion
            </Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Editar miembro">
        <form onSubmit={handleEditSave} className="space-y-4">
          <Input
            label="Nombre para mostrar"
            placeholder="Ej: Juan Perez"
            required
            value={editDisplayName}
            onChange={(e) => setEditDisplayName(e.target.value)}
          />

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
              Etiqueta / Cargo en la organización
            </label>
            <select
              value={editPositionCategory}
              onChange={(e) => setEditPositionCategory(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
            >
              {POSITION_PRESETS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {editPositionCategory === 'custom' && (
            <Input
              label="Especificar cargo o etiqueta personalizada"
              placeholder="Ej: Relaciones Públicas, Asesor, etc."
              value={editCustomPosition}
              onChange={(e) => setEditCustomPosition(e.target.value)}
              required
            />
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Roles</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-md border border-neutral-200 dark:border-neutral-800 p-3">
              {ROLE_OPTIONS.map((option) => (
                <label key={option.value} className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
                  <input
                    type="checkbox"
                    checked={editRoles.includes(option.value)}
                    onChange={() => toggleEditRole(option.value)}
                    className="rounded border-neutral-300 dark:border-neutral-700"
                  />
                  {option.label}
                </label>
              ))}
            </div>
            {editRoles.length === 0 && (
              <p className="text-xs text-danger-600">Selecciona al menos un rol.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Estado</label>
            <select
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value as any)}
              className="w-full h-10 px-3 rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
            >
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
              <option value="suspended">Suspendido</option>
            </select>
          </div>

          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Al cambiar el estado a "Inactivo" o "Suspendido", la persona perderá acceso al portal. 
            No es recomendable eliminar físicamente al miembro para mantener el historial de sus contribuciones y mensajes.
          </p>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800">
            <Button variant="outline" type="button" onClick={() => setShowEditModal(false)} disabled={isUpdating}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={isUpdating || editRoles.length === 0}>
              {isUpdating ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </div>
        </form>
      </Modal>
      {reauthModal}
    </div>
  )
}
